import { useEffect, useState } from 'react';

import { Box, Flex, Tooltip } from '@chakra-ui/react';
import styled from '@emotion/styled';
import useOrganisationSettings from 'components/hooks/useOrganisationSettings';
import { difference, uniqWith } from 'lodash';

import NoCollaboratorImg from 'assets/images/NoCollaborator.svg';

import { SEARCH_WIDGET_TYPES } from 'constants/ProjectConstants';
import { StringModifier, modifyString } from 'constants/copyHelpers';

import { searchAutoComplete } from 'services/search';

import { Avatar } from 'uiToolkitV1/Avatar';
import { Checkbox } from 'uiToolkitV1/Checkbox';
import { TextBodyMedium, TextLabel } from 'uiToolkitV1/Typography';

const StyledTextLabel = styled(TextLabel)`
  overflow: hidden;
  white-space: ${(props) => (props.wrapText ? 'normal' : 'nowrap')};
  text-overflow: ellipsis;
  margin-left: 2px;
  margin-right: 8px;
  word-break: ${(props) => (props.wrapText ? 'break-word' : 'normal')};
`;

const EmployeeListItem = ({
  displayName,
  profilePhoto,
  id,
  email = '',
  isSelected = false,
  onClick,
  isClearable = true,
  isDisabled = false,
  companyId,
  companyName,
}) => {
  const { moduleName, objectivesName, keyResultsName } = useOrganisationSettings();
  return (
    <Tooltip
      label={
        isSelected && !isClearable
          ? 'At least one owner should be selected'
          : isDisabled
            ? `User is a sole owner for this ${modifyString(
                moduleName,
                StringModifier.SENTENCE_CASE
              )}, please add another to add this user as collaborator`
            : ''
      }
    >
      <Flex
        cursor={isSelected && !isClearable ? 'not-allowed !important' : 'pointer !important'}
        key={id}
        alignItems="center"
        justifyContent="space-between"
        height="60px"
        p="12px"
        mt={1}
        borderRadius={4}
      >
        <Flex alignItems="center">
          <Avatar size={24} name={displayName} src={profilePhoto} />
          <Flex ml="12px" flexDir="column">
            <TextBodyMedium cursor="pointer" color="inherit">
              {displayName}
            </TextBodyMedium>

            {companyId && companyName ? (
              <StyledTextLabel color="text.g2" cursor="pointer" wrapText>
                {`${email} (${companyId}, ${companyName})`}
              </StyledTextLabel>
            ) : (
              <StyledTextLabel color="text.g2">{email}</StyledTextLabel>
            )}
          </Flex>
        </Flex>

        <Flex>
          {isSelected ? (
            <Checkbox isChecked onChange={onClick} disabled={!isClearable} />
          ) : (
            <Checkbox onChange={onClick} disabled={isDisabled} />
          )}
        </Flex>
      </Flex>
    </Tooltip>
  );
};
const useMatchedEmployees = ({ selectedEmployees = [], searchText = '' }) => {
  const [fetchedEmployees, setFetchedEmployees] = useState([]);
  const [matchedEmployees, setMatchedEmployees] = useState([]);

  useEffect(() => {
    const fetchEmployees = async (text) => {
      searchAutoComplete({
        tagType: 'EMPLOYEE_NAME_AND_EMAIL',
        query: text,
        callback: (data) => {
          if (typeof setFetchedEmployees === 'function' && searchText === text)
            setFetchedEmployees(
              Array.isArray(data)
                ? data.map((emp) => {
                    const { displayName, id, profilePhotoSrc, email } = emp;

                    return {
                      id,
                      displayName,
                      profilePhoto: profilePhotoSrc,
                      email,
                      isSelected: false,
                      companyId: emp.companyId,
                      companyName: emp.companyName,
                    };
                  })
                : []
            );
        },
        widgetType: SEARCH_WIDGET_TYPES.task,
        limit: 5,
      });
    };

    if (searchText === '') {
      setFetchedEmployees([]);
      return;
    }
    fetchEmployees(searchText);
  }, [searchText]);

  useEffect(() => {
    const filteredSelectedEmployees = selectedEmployees.filter(
      ({ displayName = '', email = '' }) =>
        displayName.toLowerCase().includes(searchText.toLowerCase()) ||
        email.toLowerCase().includes(searchText.toLowerCase())
    );
    const uniqueMatchedEmployees = uniqWith(
      [...filteredSelectedEmployees, ...fetchedEmployees],
      (a, b) => String(a.id) === String(b.id)
    );
    setMatchedEmployees(uniqueMatchedEmployees);
  }, [selectedEmployees, fetchedEmployees, searchText]);

  return matchedEmployees;
};

const EmptyEmployeeData = ({ isOwner, moduleName }) => (
  <Box py={2} display="flex" gap={4} flexDirection="column" justifyContent="center" alignItems="center">
    <img height="100px" src={NoCollaboratorImg} alt="NoCollaboratorImg" />
    <TextBodyMedium bold>
      {' '}
      This {modifyString(moduleName, StringModifier.SENTENCE_CASE)} does not have {isOwner ? 'Owners' : 'Collaborators'}
    </TextBodyMedium>
  </Box>
);
export const SearchEmployees = ({
  isOwner = true,
  employees = [],
  originalOwners,
  setEmployees,
  atLeastOneEmployeeToBeSelected = false,
  searchText,
}) => {
  const matchedEmployees = useMatchedEmployees({ selectedEmployees: employees, searchText });
  const { moduleName } = useOrganisationSettings();

  const handleEmployeeClick = (ev, employee) => {
    const isEmployeeAlreadyPresent = employees.findIndex(({ id }) => id === employee.id) > -1;

    if (isEmployeeAlreadyPresent && employees.length === 1 && atLeastOneEmployeeToBeSelected) return;

    if (isEmployeeAlreadyPresent) setEmployees(employees.filter(({ id }) => id !== employee.id));
    else setEmployees((prev) => [{ ...employee, isSelected: true }, ...prev]);
  };

  return (
    <Box>
      <Flex flexDirection="column" alignItems="stretch">
        {matchedEmployees?.length === 0 ? <EmptyEmployeeData isOwner={isOwner} moduleName={moduleName} /> : null}

        {matchedEmployees?.map((emp) => {
          const onClick = (e) => {
            handleEmployeeClick(e, emp);
          };

          let isOnlyOneOwnerPresent = false;

          if (!isOwner) {
            if (originalOwners.length === 1) isOnlyOneOwnerPresent = String(originalOwners[0]?.id) === String(emp.id);

            if (originalOwners.length > 1) {
              const ownerIds = originalOwners.map((owner) => String(owner.id));
              const employeeIds = employees.map((emp) => String(emp.id));

              const remainingOwnerIds = difference(ownerIds, employeeIds);

              if (remainingOwnerIds.length === 1) {
                isOnlyOneOwnerPresent = remainingOwnerIds[0] === String(emp.id);
              }
            }
          }

          return (
            <EmployeeListItem
              key={emp.id}
              {...emp}
              onClick={onClick}
              isDisabled={isOnlyOneOwnerPresent}
              /* following condition stops last employee to be cleared but lets user select the employee if no employee is selected */
              isClearable={atLeastOneEmployeeToBeSelected ? employees.length !== 1 : true}
            />
          );
        })}
      </Flex>
    </Box>
  );
};
